(function () {
  'use strict';

  /** Safe publish helper — no-ops silently if the API isn't available. */
  function publish(eventName, payload) {
    try {
      if (
        window.Shopify &&
        window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === 'function'
      ) {
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (_) {
      /* analytics should never break the page */
    }
  }

  /**
   * 1. SEARCH RESULT CLICKED
   * Detects clicks on products when a search query is present in URL.
   */
  document.addEventListener('click', function (e) {
    try {
      var target = /** @type {Element} */ (e.target);
      if (!target || typeof target.closest !== 'function') return;

      var isSearchPage =
        window.location.pathname.includes('/search') ||
        new URLSearchParams(window.location.search).has('q');

      var productCard = target.closest('.product-card, .search-result-item, .grid-view-item');

      if (isSearchPage && productCard) {
        var link = productCard.querySelector('a');
        var href = link ? link.href : '';

        var allCards = Array.from(
          document.querySelectorAll('.product-card, .search-result-item, .grid-view-item')
        );
        var rank = allCards.indexOf(productCard) + 1;

        publish('custom_search_result_clicked', {
          productUrl: href,
          rank: rank,
          searchTerm: new URLSearchParams(window.location.search).get('q'),
        });
      }
    } catch (_) {}
  });

  /**
   * 2. INTERNAL CAMPAIGN CLICKED
   * Looks for elements tagged with internal campaign data attributes. The theme
   * renders these as `data-internal-campaign-name`, `data-internal-campaign-id`,
   * and `data-internal-campaign-placement` (see snippets/button.liquid,
   * blocks/link.liquid, snippets/visual-filters.liquid, etc.). The three values
   * can live on a single element (button/link blocks) or be spread across nested
   * elements within one clickable (the "Shop By" visual filters), so collect each
   * value from the campaign container or any of its descendants.
   *
   * Usage:
   *   <a href="..." data-internal-campaign-name="Summer Sale"
   *      data-internal-campaign-id="123" data-internal-campaign-placement="Hero Banner">
   *
   * AI-generated theme blocks render CTAs without these attributes, so as a
   * fallback any link click inside an AI block is still tracked with a derived
   * campaignName (the link label) and placement (the page handle).
   *
   * Replo landing pages get the same treatment (ECOMDEV-923). Replo can render the
   * three attributes natively — Config tab → custom data attributes, which Replo
   * prefixes with `data-` for you — but only on Button, Container, Product
   * Container, and Text components, so every other CTA falls back to derived
   * values. On a Replo page the derived campaignId is the component's `data-rid`,
   * the only stable per-component identifier Replo emits.
   *
   * The payload `source` field distinguishes 'attributes' (configured) from the
   * derived paths: 'ai-block', 'content-block', and 'replo'.
   */
  var CAMPAIGN_SELECTOR =
    '[data-internal-campaign-name],[data-internal-campaign-id],[data-internal-campaign-placement]';

  // Shopify AI-generated theme blocks (blocks/ai_gen_block_*.liquid) render bare
  // CTAs with no campaign attributes — sometimes inside merchant rich text with no
  // class at all — so they can't be tagged per-anchor. Every element such a block
  // renders carries the block id in its class (ai_gen_id = block.id stripped of
  // underscores), and Shopify's AI block ids always contain "aigenblock". Matching
  // that substring lets us auto-attribute clicks on any AI-block CTA, current or
  // future, without editing each block.
  var AI_BLOCK_SELECTOR = '[class*="aigenblock"]';

  // Replo landing pages (templates/page.replo.*.liquid) render their entire body
  // inside #replo-fullpage-element. The theme header, footer, and nav sit outside
  // it, so scoping to this element keeps ordinary site chrome from firing campaign
  // events without needing the header/footer/nav exclusions the tile path uses.
  var REPLO_PAGE_SELECTOR = '#replo-fullpage-element';

  // Replo's own carousel chrome. Arrows are real <button>s carrying
  // data-replo-part="nav-button"; the dots are divs, excluded already by only
  // matching anchors and buttons. Slides themselves are data-replo-part="slide",
  // which is deliberately absent here — a CTA inside a slide is a real CTA.
  var REPLO_CONTROL_PARTS = ['nav-button', 'indicator'];

  // Variant pickers and dropdowns render their choices as focusable children with
  // button semantics. Selecting a size is not a campaign click, so anything inside
  // one of these widgets is skipped.
  var REPLO_INPUT_WIDGET_SELECTOR = '[role="radiogroup"],[role="listbox"]';

  /** Read an attribute from the element itself or its first matching descendant. */
  function readCampaignAttr(scope, attr) {
    if (scope.hasAttribute(attr)) return scope.getAttribute(attr);
    var inner = scope.querySelector('[' + attr + ']');
    return inner ? inner.getAttribute(attr) : null;
  }

  /** Human-readable label for a link: visible text → aria-label → image alt. */
  function getLinkLabel(link) {
    var text = (link.textContent || '').replace(/\s+/g, ' ').trim();
    if (text) return text;
    var ariaLabel = link.getAttribute('aria-label');
    if (ariaLabel && ariaLabel.trim()) return ariaLabel.trim();
    var img = link.querySelector('img[alt]');
    if (img && img.getAttribute('alt')) return img.getAttribute('alt').trim();
    return null;
  }

  /** Current page handle from the path: /pages/magnesium-1 → "magnesium-1", "/" → "home". */
  function getPageHandle() {
    try {
      var path = window.location.pathname.replace(/\/+$/, '');
      if (!path) return 'home';
      var segments = path.split('/');
      return segments[segments.length - 1] || 'home';
    } catch (_) {
      return 'unknown';
    }
  }

  /** Is this a Replo carousel arrow rather than a merchandised CTA? */
  function isReploControl(el) {
    var part = el.closest('[data-replo-part]');
    if (!part) return false;
    return REPLO_CONTROL_PARTS.indexOf(part.getAttribute('data-replo-part')) !== -1;
  }

  /**
   * Stable Replo component id for a clicked CTA. Replo emits no human-readable
   * component name anywhere in the DOM, but every node it renders carries a
   * data-rid uuid that is stable across copy edits and restyles — it is what lets
   * Amplitude tell two identically-labelled "Shop Now" buttons apart.
   */
  function getReploComponentId(el) {
    var node = el.closest('[data-rid]');
    return node ? node.getAttribute('data-rid') : null;
  }

  /**
   * Is this link part of a promotional media tile built from first-party blocks
   * (the "Explore More" style image + title carousels — icon/text inside group
   * blocks)? We treat a link as a tile CTA when it sits inside an icon-block
   * (the image link) or inside a group-block whose subtree also contains an
   * icon-block (the title link beside the image). Header/footer/nav are excluded
   * so ordinary chrome and body prose links don't fire campaign events.
   */
  function isPromoTileLink(link) {
    if (link.closest('header, footer, nav')) return false;
    if (link.closest('.icon-block')) return true;
    var group = link.closest('.group-block');
    while (group) {
      if (group.querySelector('.icon-block')) return true;
      var parent = group.parentElement;
      group = parent ? parent.closest('.group-block') : null;
    }
    return false;
  }

  function handleInternalCampaignClick(e) {
    try {
      var target = /** @type {Element} */ (e.target);
      if (!target || typeof target.closest !== 'function') return;

      // 1) Explicit, merchant-configured campaign attributes (highest fidelity).
      var tagged = target.closest(CAMPAIGN_SELECTOR);
      if (tagged) {
        // Anchor the campaign on the nearest clickable so values that live on
        // sibling/child nodes (e.g. visual filters) are all in scope.
        var container = tagged.closest('a, button, [role="button"]') || tagged;
        // Prefer the link actually clicked: when the campaign attribute lives on a
        // wrapper around many links (e.g. a recommendations carousel), this reports
        // the clicked product's URL rather than the container's first link.
        var anchor =
          target.closest('a[href]') ||
          (container.matches('a[href]') ? container : container.querySelector('a[href]'));

        publish('custom_internal_campaign_clicked', {
          campaignName: readCampaignAttr(container, 'data-internal-campaign-name'),
          campaignId: readCampaignAttr(container, 'data-internal-campaign-id'),
          placement: readCampaignAttr(container, 'data-internal-campaign-placement') || 'unknown',
          targetUrl: anchor ? anchor.getAttribute('href') : null,
          source: 'attributes',
        });
        return;
      }

      // 2) Replo landing pages. Replo can render the explicit attributes above —
      //    a merchandiser adds them as key/value pairs in the component's Config
      //    tab, where the "data-" prefix is applied automatically — but only on
      //    Button, Container, Product Container, and Text components. Images,
      //    icons, and links inside rich text cannot be tagged at all, and nothing
      //    obliges anyone to tag a new page before it goes live. Derive an event
      //    for every CTA on a Replo page so a new LP reports clicks on day one;
      //    the explicit path above still wins wherever it is configured.
      //
      //    Only anchors and real <button>s count. Replo's role="button" nodes are
      //    carousel dots, video mute controls, and variant swatches — chrome, not
      //    merchandising — so they are left out on purpose.
      if (target.closest(REPLO_PAGE_SELECTOR)) {
        var reploCta = target.closest('a[href], button');
        if (!reploCta) return;
        if (isReploControl(reploCta)) return;
        if (reploCta.closest(REPLO_INPUT_WIDGET_SELECTOR)) return;

        publish('custom_internal_campaign_clicked', {
          campaignName: getLinkLabel(reploCta),
          campaignId: getReploComponentId(reploCta),
          placement: getPageHandle(),
          // null for Add to Cart and other in-page buttons, which have no href.
          targetUrl: reploCta.getAttribute('href'),
          source: 'replo',
        });
        return;
      }

      // 3) Fallback for CTAs that carry no campaign attributes: AI-generated
      //    blocks and first-party promo tiles (e.g. "Explore More" carousels).
      //    Derive the campaign name from the link label and placement from the page.
      var link = target.closest('a[href]');
      if (!link) return;

      var source = null;
      if (link.closest(AI_BLOCK_SELECTOR)) source = 'ai-block';
      else if (isPromoTileLink(link)) source = 'content-block';
      if (!source) return;

      publish('custom_internal_campaign_clicked', {
        campaignName: getLinkLabel(link),
        campaignId: null,
        placement: getPageHandle(),
        targetUrl: link.getAttribute('href'),
        source: source,
      });
    } catch (_) {}
  }

  // Capture phase, not bubble (ECOMDEV-923 follow-up). Replo wraps its components in
  // handlers that call stopPropagation() on click: on the Shop by Ingredient pages the
  // event dies at a Replo container a few levels below <body>, so a bubble-phase
  // listener never saw it. Every CTA on /pages/shop-by-ingredient-2 was correctly
  // tagged and none reported; the single tile that did report was the one CTA Replo
  // happens to render as a rich-text link, which bubbles normally. Capture runs from
  // the document down, before any component handler can stop the event, so tracking no
  // longer depends on what a page's components do with propagation. This handler only
  // reads and publishes — it never calls preventDefault or stopPropagation — so firing
  // it earlier changes nothing for the page itself.
  document.addEventListener('click', handleInternalCampaignClick, true);

  /**
   * 3. PRODUCT LISTING FILTERED
   * Listens for changes on inputs inside collection filters.
   * Adjust '#CollectionFilters' to your theme's filter container ID/Class.
   */
  var filterContainer = document.querySelector('#CollectionFilters, .facets');
  if (filterContainer) {
    var filterDebounce = 0; // scoped timer — avoids polluting window

    filterContainer.addEventListener('change', function (e) {
      try {
        var el = /** @type {HTMLInputElement | HTMLSelectElement} */ (e.target);
        if (!el) return;
        var tag = el.tagName;
        if (tag !== 'INPUT' && tag !== 'SELECT') return;

        clearTimeout(filterDebounce);
        filterDebounce = setTimeout(function () {
          publish('custom_product_listing_filtered', {
            filterName: el.name,
            filterValue: el.value,
            collectionUrl: window.location.pathname,
          });
        }, 500);
      } catch (_) {}
    });
  }
})();
